// Ported from genieos/web/src/hooks/useHistorianShortcuts.ts @ 84923d036
/**
 * Keyboard Shortcuts Hook for Historian Data Viewer
 *
 * Provides global keyboard shortcuts for common actions:
 * - Ctrl/Cmd + E: Export
 * - Ctrl/Cmd + R: Refresh
 * - Ctrl/Cmd + F: Focus search
 * - Escape: Close modals
 */

import { useEffect, useCallback } from 'react';

interface ShortcutHandlers {
  onExport?: () => void;
  onRefresh?: () => void;
  onSearch?: () => void;
  onEscape?: () => void;
}

export function useHistorianShortcuts(handlers: ShortcutHandlers) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Ignore if user is typing in an input (except for Escape)
      const target = event.target as HTMLElement;
      const isInputElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (isInputElement) {
        // Only handle Escape in inputs
        if (event.key === 'Escape' && handlers.onEscape) {
          handlers.onEscape();
          event.preventDefault();
        }
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? event.metaKey : event.ctrlKey;

      // Ctrl/Cmd + E: Export
      if (modKey && event.key.toLowerCase() === 'e') {
        event.preventDefault();
        handlers.onExport?.();
        return;
      }

      // Ctrl/Cmd + R: Refresh (override browser refresh)
      if (modKey && event.key.toLowerCase() === 'r') {
        event.preventDefault();
        handlers.onRefresh?.();
        return;
      }

      // Ctrl/Cmd + F: Focus search (override browser find)
      if (modKey && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        handlers.onSearch?.();
        return;
      }

      // Escape: Close modals
      if (event.key === 'Escape') {
        handlers.onEscape?.();
        return;
      }
    },
    [handlers]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

export default useHistorianShortcuts;
